import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

// Paste a customer e-mail → see exactly what ChatGPT would answer (never auto-sends).
export function SimulateDialog() {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const run = useMutation({
    mutationFn: () => api.post("/ai-email/simulate", { from_email: from.trim(), from_name: name.trim(), subject: subject.trim(), body }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["ai-inbox"] });
      toast.success(r.data.would_auto_send ? t("ChatGPT would have answered this one automatically") : t("Draft ready — held for review"));
      setOpen(false); setBody(""); setSubject("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Simulation failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="ai-simulate-btn">🧪 {t("Test with a pasted e-mail")}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="ai-simulate-dialog">
        <DialogHeader><DialogTitle>🧪 {t("Test ChatGPT with a customer e-mail")}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">{t("Paste a real customer e-mail. ChatGPT looks the sender up in the CRM and drafts the answer. Nothing is sent — you decide in the inbox.")}</p>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">{t("Customer e-mail")}</Label><Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="jane@example.com" data-testid="ai-sim-from" /></div>
            <div><Label className="text-xs">{t("Name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane" data-testid="ai-sim-name" /></div>
          </div>
          <div><Label className="text-xs">{t("Subject")}</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("Where is my order?")} data-testid="ai-sim-subject" /></div>
          <div><Label className="text-xs">{t("E-mail text")}</Label><Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} data-testid="ai-sim-body" placeholder={t("Hi, I ordered 3 weeks ago (order #5100) — any update?")} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="ai-sim-cancel">{t("Cancel")}</Button>
          <Button onClick={() => run.mutate()} disabled={run.isPending || !from.trim() || !body.trim()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ai-sim-run">
            {run.isPending ? t("Asking ChatGPT…") : t("Draft the answer")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
